import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Button } from "@wellcheck/components/ui/button";
import { Skeleton } from "@wellcheck/components/ui/skeleton";

import { AbandonedScreen } from "./AbandonedScreen";
import { AnswerClosedScreen } from "./AnswerClosedScreen";
import { QaAutofillPanel } from "./QaAutofillPanel";
import { ReviewScreen } from "./ReviewScreen";
import { SubmittedScreen } from "./SubmittedScreen";
import { SurveySectionView } from "./SurveySectionView";
import { ThanksScreen } from "./ThanksScreen";

import type {
  AnswerValue,
  AnswersDraft,
  Question,
  SurveySection,
} from "@/features/respond/data/types";

import { WizardProgress } from "@/components/WizardProgress";
import { isAnswerClosedError } from "@/features/respond/api/answerDeadline";
import {
  mapAnswersToSubmitItems,
  mapQuestionnaireToCampaign,
} from "@/features/respond/api/mapQuestionnaire";
import { isSubmitNotAllowedError } from "@/features/respond/api/submitNotAllowed";
import { useQuestionnaire } from "@/features/respond/api/useQuestionnaire";
import { useSubmitAnswers } from "@/features/respond/api/useSubmitAnswers";
import { useDraftAnswers } from "@/features/respond/hooks/useDraftAnswers";
import { DEFAULT_LOCALE, isSupportedLocale } from "@/i18n/config";

interface SurveyFormProps {
  invitationId: string;
}

type Phase = { kind: "section"; index: number } | { kind: "review" } | { kind: "thanks" };

/**
 * SurveyForm — アンケート全体の進行を司る。
 *
 * 紙票のフォーマットに合わせて section (A〜D) を 1 画面ずつ表示し、最後に Review → Thanks。
 * section ごとに必須質問が全て埋まっていないと「次へ」が押せない。
 *
 * 設問データは GET /api/respondent-questionnaire (ADR-0024) から取得する。取得中は
 * Skeleton、取得失敗時は再試行導線を出す。送信は POST /api/respondent-answers。
 * 409 (二重提出) はサーバ側の状態が既に「提出済み」であることを意味するため SubmittedScreen に
 * 倒す。回答期間終了 (ADR-0032, detail: "answer period closed") は AnswerClosedScreen、
 * 提出不可 (ADR-0054, detail: "submission not allowed in current status") は AbandonedScreen に
 * それぞれ倒す (isAnswerClosedError / isSubmitNotAllowedError で detail 文字列を判別する)。
 */
export function SurveyForm({ invitationId }: SurveyFormProps) {
  const { t } = useTranslation("respond");
  const questionnaireQuery = useQuestionnaire();
  const { answers, setAnswer, setAnswers, clearDraft } = useDraftAnswers(invitationId);
  const [phase, setPhase] = useState<Phase>({ kind: "section", index: 0 });
  const submitAnswers = useSubmitAnswers();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = isSupportedLocale(lang) ? lang : DEFAULT_LOCALE;

  const campaign = useMemo(
    () =>
      questionnaireQuery.data
        ? mapQuestionnaireToCampaign(questionnaireQuery.data, t("campaignTitle"))
        : undefined,
    [questionnaireQuery.data, t],
  );

  const sectionSteps = useMemo(
    () => (campaign?.sections ?? []).map(() => ({ label: "" })),
    [campaign?.sections],
  );

  if (questionnaireQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (questionnaireQuery.isError || !campaign) {
    return (
      <section className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
        <h2 className="text-base font-semibold">{t("form.loadError")}</h2>
        <p className="text-sm text-muted-foreground">{t("form.loadErrorDescription")}</p>
        <Button variant="outline" onClick={() => void questionnaireQuery.refetch()}>
          {t("form.retry")}
        </Button>
      </section>
    );
  }

  // 送信成功後の invalidate による再 fetch が completed で解決しても、今この画面で
  // 提出した本人には ThanksScreen を出すため、completed 判定より先に見る (#311)。
  if (phase.kind === "thanks") {
    return <ThanksScreen />;
  }

  // GET 応答時点で既に completed だった場合 (RespondPage のセッション判定と
  // 別経路で completed になるケース、例えば別タブで先に提出完了した場合) も
  // SubmittedScreen に倒す。
  if (questionnaireQuery.data?.status === "completed") {
    return <SubmittedScreen />;
  }

  if (submitAnswers.isError && submitAnswers.error.status === 409) {
    // 回答期間終了 (ADR-0032) / 提出不可 (ADR-0054) は二重提出と文言を分ける。
    // 判定順序が重要: detail 文字列判定より先に status===409 で SubmittedScreen に
    // 倒すと両ケースが握りつぶされるため、detail 文字列の判定を先に行う。
    if (isAnswerClosedError(submitAnswers.error)) {
      return <AnswerClosedScreen />;
    }
    if (isSubmitNotAllowedError(submitAnswers.error)) {
      return <AbandonedScreen />;
    }
    return <SubmittedScreen />;
  }

  const currentSectionTitle =
    phase.kind === "section"
      ? (campaign.sections[phase.index]?.title ?? "")
      : t("form.reviewSectionTitle");

  if (phase.kind === "review") {
    return (
      <ReviewScreen
        campaign={campaign}
        answers={answers}
        progress={
          <WizardProgress
            steps={sectionSteps}
            currentIndex={campaign.sections.length}
            onStepClick={(i) => {
              setPhase({ kind: "section", index: i });
              window.scrollTo({ top: 0 });
            }}
            isStepNavigable={() => true}
          />
        }
        onBack={() => setPhase({ kind: "section", index: campaign.sections.length - 1 })}
        submitError={
          submitAnswers.isError && submitAnswers.error.status !== 409
            ? t("review.submitError")
            : undefined
        }
        isSubmitting={submitAnswers.isPending}
        onSubmit={() => {
          const items = mapAnswersToSubmitItems(campaign, answers);
          submitAnswers.mutate(
            { items, lang: currentLang },
            {
              onSuccess: () => {
                clearDraft();
                setPhase({ kind: "thanks" });
              },
            },
          );
        }}
      />
    );
  }

  const section = campaign.sections[phase.index];
  if (!section) {
    // 異常系: out-of-range。先頭へ戻す
    setPhase({ kind: "section", index: 0 });
    return null;
  }
  const isLastSection = phase.index === campaign.sections.length - 1;
  const answeredCount = countAnswered(section.questions, answers);
  const totalCount = section.questions.length;
  const canProceed = answeredCount === totalCount;
  const ratio = totalCount === 0 ? 0 : answeredCount / totalCount;

  return (
    <div className="space-y-10">
      <div className="sticky top-0 z-10 -mx-6 space-y-3 border-b border-border bg-background/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <WizardProgress
          steps={sectionSteps}
          currentIndex={phase.index}
          onStepClick={(i) => {
            setPhase({ kind: "section", index: i });
            window.scrollTo({ top: 0 });
          }}
          isStepNavigable={(i) => {
            if (i === phase.index) return false;
            if (i < phase.index) return true;
            // 未来のステップは、間の section が全問回答済みのときだけ飛べる
            for (let j = phase.index; j < i; j += 1) {
              const sec = campaign.sections[j];
              if (!sec) return false;
              if (countAnswered(sec.questions, answers) !== sec.questions.length) return false;
            }
            return true;
          }}
        />
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate text-base">
              <span className="mr-2 text-sm text-muted-foreground">{t("form.currentSection")}</span>
              <span className="font-medium">{currentSectionTitle}</span>
            </p>
            <span className="shrink-0 tabular-nums">
              <span className="text-lg font-semibold text-primary">{answeredCount}</span>
              <span className="text-muted-foreground">
                {" "}
                {t("form.answeredCount", { total: totalCount })}
              </span>
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${ratio * 100}%` }}
            />
          </div>
        </div>
        {import.meta.env.DEV && (
          <div className="flex items-center gap-2 border-t border-dashed border-border pt-2 text-xs">
            <span className="text-muted-foreground">[dev]</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fillSection(section, setAnswer)}
            >
              {t("form.devFillSection")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                campaign.sections.forEach((s) => fillSection(s, setAnswer));
                setPhase({ kind: "review" });
              }}
            >
              {t("form.devFillAll")}
            </Button>
          </div>
        )}
      </div>

      {import.meta.env.VITE_ENABLE_QA_TOOLS === "true" && (
        <QaAutofillPanel
          sections={campaign.sections}
          onFillAll={(nextAnswers) => {
            setAnswers(nextAnswers);
            setPhase({ kind: "review" });
          }}
        />
      )}

      <SurveySectionView section={section} answers={answers} onAnswer={setAnswer} />

      <div className="flex items-center justify-end gap-6 border-t border-border pt-6">
        {phase.index > 0 && (
          <Button
            variant="link"
            onClick={() => setPhase({ kind: "section", index: phase.index - 1 })}
            className="px-0 text-base text-muted-foreground"
          >
            {t("form.back")}
          </Button>
        )}
        <Button
          size="lg"
          disabled={!canProceed}
          className="h-14 px-8 text-lg"
          onClick={() => {
            if (isLastSection) {
              setPhase({ kind: "review" });
            } else {
              setPhase({ kind: "section", index: phase.index + 1 });
              window.scrollTo({ top: 0 });
            }
          }}
        >
          {isLastSection ? t("form.confirm") : t("form.next")}
        </Button>
      </div>
    </div>
  );
}

function countAnswered(questions: ReadonlyArray<Question>, answers: AnswersDraft): number {
  return questions.reduce((acc, q) => {
    const v = answers[q.id];
    return v !== undefined && v !== "" ? acc + 1 : acc;
  }, 0);
}

function fillSection(
  section: SurveySection,
  setAnswer: (id: string, value: AnswerValue) => void,
): void {
  for (const q of section.questions) {
    if (q.type === "four-scale") {
      setAnswer(q.id, section.scale?.[0]?.value ?? 1);
    } else if (q.type === "single-choice") {
      setAnswer(q.id, q.choices[0]?.value ?? "");
    } else if (q.type === "text") {
      setAnswer(q.id, "dev sample");
    }
  }
}
